/**
 * Página de listagem de fluxos de automação
 * Design moderno com gradientes, efeitos hover, badges com glow e cards elegantes
 * 
 * PERMISSÕES GRANULARES:
 * - canCreate('flows'): criar novos fluxos
 * - canEdit('flows'): editar nome, duplicar, configurar gatilhos
 * - canDelete('flows'): excluir fluxos
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Plus, 
  Play, 
  Pause, 
  Copy, 
  Trash2, 
  Pencil,
  Settings,
  GitBranch,
  Clock,
  CheckCircle,
  XCircle,
  MoreVertical,
  Eye,
  Archive,
  Boxes,
  LayoutGrid,
  List,
  Webhook,
  ListChecks,
  Download,
  Upload,
  Search,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { createLogger } from '@/lib/logger';
import { toast } from '@/hooks/use-toast';
import { useFlowExecutionStats } from '@/hooks/useFlowExecutionStats';
import { EditFlowDialog } from '@/components/tenant/flows/EditFlowDialog';
import { ConfigureTriggersDialog } from '@/components/tenant/flows/config/ConfigureTriggersDialog';
import { FlowListRow } from '@/components/tenant/flows/FlowListRow';
import { FlowWebhooksList } from '@/components/tenant/flows/FlowWebhooksList';
import { FlowWebhookDetailDialog } from '@/components/tenant/flows/FlowWebhookDetailDialog';
import { ExecutionDetailsDialog } from '@/components/tenant/flows/ExecutionDetailsDialog';
import { TemplateExportDialog } from '@/components/template-system/TemplateExportDialog';
import { buildFlowSelection } from '@/lib/template-system/scopedSelection';
import { ImportFlowDialog } from '@/components/tenant/flows/ImportFlowDialog';
import type { Tables } from '@/integrations/supabase/types';
import type { TriggerConfig } from '@/types/flows';
import type { TemplateCollectionItems } from '@/lib/template-system/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

// Tipo para modo de visualização
type ViewMode = 'cards' | 'list';

const logger = createLogger('Flows');

/**
 * Configuração visual por status do fluxo
 */
const statusConfig: Record<string, {
  label: string;
  icon: typeof Play;
  borderColor: string;
  badgeClass: string;
  glowClass: string;
}> = {
  draft: {
    label: 'Rascunho',
    icon: Clock,
    borderColor: 'border-t-muted-foreground/50',
    badgeClass: 'bg-muted text-muted-foreground border border-muted-foreground/30',
    glowClass: '',
  },
  active: {
    label: 'Ativo',
    icon: Play,
    borderColor: 'border-t-green-500',
    badgeClass: 'bg-green-500/10 text-green-600 border border-green-500/30',
    glowClass: 'shadow-[0_0_10px_rgba(34,197,94,0.3)]',
  },
  paused: {
    label: 'Pausado',
    icon: Pause,
    borderColor: 'border-t-yellow-500',
    badgeClass: 'bg-yellow-500/10 text-yellow-600 border border-yellow-500/30',
    glowClass: 'shadow-[0_0_10px_rgba(234,179,8,0.3)]',
  },
  archived: {
    label: 'Arquivado',
    icon: Archive,
    borderColor: 'border-t-muted',
    badgeClass: 'bg-muted text-muted-foreground border border-muted-foreground/30',
    glowClass: '',
  },
};

/**
 * Página de listagem de fluxos de automação
 */
export default function Flows() {
  const navigate = useNavigate();
  const { currentTenant } = useAuth();
  const { canCreate, canEdit, canDelete } = usePermissions();
  const queryClient = useQueryClient();
  
  // Estado para aba principal (Geral/Webhook)
  const [mainTab, setMainTab] = useState<'general' | 'webhook'>('general');
  const [activeTab, setActiveTab] = useState<'all' | 'draft' | 'active' | 'paused'>('all');
  const [editingFlow, setEditingFlow] = useState<Tables<"flows"> | null>(null);
  const [deletingFlow, setDeletingFlow] = useState<Tables<"flows"> | null>(null);
  const [configuringFlow, setConfiguringFlow] = useState<Tables<"flows"> | null>(null);
  
  // Estado para modal de webhook de integração
  const [webhookDetailFlow, setWebhookDetailFlow] = useState<Tables<"flows"> | null>(null);
  
  // Estado para modal de exportação
  const [exportingFlow, setExportingFlow] = useState<Tables<"flows"> | null>(null);

  /** Módulo usado pelo atalho "exportar este fluxo" (memorizado por estabilidade). */
  const exportSelectionModules = useMemo(() => ['flows' as const], []);

  /** Monta a seleção do assistente contendo apenas o fluxo escolhido. */
  const buildExportSelection = useCallback(
    (itemsByModule: Record<string, TemplateCollectionItems[]>) =>
      buildFlowSelection(exportingFlow?.name ?? '', itemsByModule),
    [exportingFlow],
  );
  
  // Estado para modal de importação
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  
  // Estado para dialog de detalhes de execução
  const [executionDialogOpen, setExecutionDialogOpen] = useState(false);
  const [executionDialogMode, setExecutionDialogMode] = useState<'today' | 'errors'>('today');
  
  // Estado para modo de visualização com persistência em localStorage
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('flows_viewMode');
    return (saved === 'cards' || saved === 'list') ? saved : 'cards';
  });

  // Estado do input de pesquisa (filtra por nome, descrição ou código de 8 chars)
  const [searchTerm, setSearchTerm] = useState('');

  // Permissões granulares para fluxos
  const canCreateFlow = canCreate('flows');
  const canEditFlow = canEdit('flows');
  const canDeleteFlow = canDelete('flows');

  /**
   * Handler para alternar modo de visualização com persistência
   */
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('flows_viewMode', mode);
  };

  /**
   * Busca fluxos do tenant atual
   */
  const { data: flows = [], isLoading, error } = useQuery({
    queryKey: ['flows', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) {
        logger.warn('Tentativa de buscar fluxos sem tenant');
        return [];
      }
      
      logger.info('Buscando fluxos do tenant', { tenantId: currentTenant.id });
      
      // Arquitetura server-side: usa RPC list_tenant_flows ao invés de SELECT direto na tabela
      const { data, error } = await supabase.rpc('list_tenant_flows', {
        p_tenant_hint: currentTenant.id,
      });
      
      if (error) {
        logger.error('Erro ao buscar fluxos', { error });
        throw error;
      }
      
      logger.debug('Fluxos carregados', { count: data?.length || 0 });
      return data || [];
    },
    enabled: !!currentTenant?.id,
  });

  /**
   * Busca estatísticas de execução de fluxos (execuções hoje, taxa de conclusão, erros)
   * tenant_id é obtido internamente no backend via auth.uid() - segurança aprimorada
   */
  const { data: executionStats } = useFlowExecutionStats();

  /**
   * Calcula métricas baseadas nos fluxos carregados e estatísticas de execução
   */
  const metrics = useMemo(() => {
    const activeCount = flows.filter(f => f.status === 'active').length;
    const draftCount = flows.filter(f => f.status === 'draft').length;
    const pausedCount = flows.filter(f => f.status === 'paused').length;
    
    return {
      active: activeCount,
      draft: draftCount,
      paused: pausedCount,
      executions: executionStats?.executions || 0,
      completionRate: executionStats?.completionRate || 0,
      errors: executionStats?.errors || 0,
    };
  }, [flows, executionStats]);

  /**
   * Aplica o filtro de busca (nome, descrição ou código de 8 chars do UUID)
   * antes do split Geral/Webhook, garantindo que contadores das abas
   * reflitam o resultado da busca.
   */
  const searchedFlows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return flows;
    return flows.filter((flow) => {
      const name = (flow.name || '').toLowerCase();
      const description = (flow.description || '').toLowerCase();
      const code = flow.id.substring(0, 8).toLowerCase();
      return name.includes(term) || description.includes(term) || code.includes(term);
    });
  }, [flows, searchTerm]);

  /**
   * Separa fluxos em Geral e Webhook baseado no trigger_config
   */
  const { generalFlows, webhookFlows } = useMemo(() => {
    const general: Tables<'flows'>[] = [];
    const webhook: Tables<'flows'>[] = [];

    searchedFlows.forEach(flow => {
      const triggers = flow.trigger_config as unknown as Array<{ type: string }> | null;
      const hasWebhookTrigger = Array.isArray(triggers) && triggers.some(t => t.type === 'webhook');

      if (hasWebhookTrigger) {
        webhook.push(flow);
      } else {
        general.push(flow);
      }
    });

    return { generalFlows: general, webhookFlows: webhook };
  }, [searchedFlows]);

  /**
   * Filtra fluxos baseado na tab ativa (aplicado à aba Geral)
   */
  const filteredFlows = useMemo(() => {
    if (activeTab === 'all') return generalFlows;
    return generalFlows.filter(f => f.status === activeTab);
  }, [generalFlows, activeTab]);

  // Mutation para duplicar fluxo
  const duplicateMutation = useMutation({
    mutationFn: async (flow: Tables<"flows">) => {
      logger.info('Duplicando fluxo', { flowId: flow.id });

      const { data, error } = await supabase
        .from('flows')
        .insert({
          tenant_id: flow.tenant_id,
          name: `${flow.name} (cópia)`,
          description: flow.description,
          status: 'draft',
          nodes: flow.nodes,
          edges: flow.edges,
          variables: flow.variables,
          trigger_config: flow.trigger_config,
          settings: flow.settings,
          version: 1,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: 'Fluxo duplicado',
        description: 'Uma cópia do fluxo foi criada com sucesso.',
      });
      queryClient.invalidateQueries({ queryKey: ['flows'] });
    },
    onError: (error) => {
      logger.error('Erro ao duplicar fluxo', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao duplicar',
        description: 'Não foi possível criar uma cópia do fluxo.',
      });
    },
  });

  // Mutation para deletar fluxo
  const deleteMutation = useMutation({
    mutationFn: async (flowId: string) => {
      logger.info('Deletando fluxo', { flowId });

      const { error } = await supabase
        .from('flows')
        .delete()
        .eq('id', flowId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: 'Fluxo excluído',
        description: 'O fluxo foi removido com sucesso.',
      });
      queryClient.invalidateQueries({ queryKey: ['flows'] });
      setDeletingFlow(null);
    },
    onError: (error) => {
      logger.error('Erro ao deletar fluxo', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao excluir',
        description: 'Não foi possível remover o fluxo.',
      });
    },
  });

  const handleEdit = (flow: Tables<'flows'>) => {
    setEditingFlow(flow);
  };

  const handleDuplicate = (flow: Tables<'flows'>) => {
    duplicateMutation.mutate(flow);
  };

  const handleDelete = (flow: Tables<'flows'>) => {
    setDeletingFlow(flow);
  };

  const handleSettings = (flow: Tables<'flows'>) => {
    setConfiguringFlow(flow);
  };

  const handleExport = (flow: Tables<'flows'>) => {
    setExportingFlow(flow);
  };

  const confirmDelete = () => {
    if (deletingFlow) {
      deleteMutation.mutate(deletingFlow.id);
    }
  };

  /**
   * Exibe toast de erro se houver falha ao carregar
   */
  if (error) {
    toast({
      variant: 'destructive',
      title: 'Erro ao carregar fluxos',
      description: 'Não foi possível buscar os fluxos. Tente novamente.',
    });
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header Compacto - padrão Contatos */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Automações</h1>
          <p className="text-xs text-muted-foreground">
            Gerencie fluxos visuais de automação para atendimento
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canCreateFlow && (
            <>
              <Button 
                variant="outline"
                size="xs"
                onClick={() => setImportDialogOpen(true)}
              >
                <Upload className="mr-1.5" />
                Importar
              </Button>
              <Button 
                size="xs"
                onClick={() => navigate('/tenant/flows/new')}
              >
                <Plus className="mr-1.5" />
                Novo Fluxo
              </Button>
            </>
          )}
        </div>
      </div>

      {/* KPI Cards Modernos */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3">
        <MetricCard
          title="FLUXOS ATIVOS"
          value={isLoading ? '-' : metrics.active}
          icon={Play}
          variant="primary"
        />
        <MetricCard
          title="EXECUÇÕES HOJE"
          value={isLoading ? '-' : metrics.executions}
          icon={GitBranch}
          variant="blue"
          clickable
          onClick={() => {
            setExecutionDialogMode('today');
            setExecutionDialogOpen(true);
          }}
        />
        <MetricCard
          title="TAXA DE CONCLUSÃO"
          value={isLoading ? '-' : `${metrics.completionRate}%`}
          icon={CheckCircle}
          variant="green"
        />
        <MetricCard
          title="COM ERROS"
          value={isLoading ? '-' : metrics.errors}
          icon={XCircle}
          variant="red"
          clickable
          onClick={() => {
            setExecutionDialogMode('errors');
            setExecutionDialogOpen(true);
          }}
        />
      </div>

      {/* Abas principais: Geral e Webhook */}
      <Tabs value={mainTab} onValueChange={(v) => setMainTab(v as typeof mainTab)}>
        <TabsList className="grid w-full grid-cols-2 max-w-md mb-4">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <ListChecks className="h-4 w-4" />
            Geral
            <Badge variant="secondary" className="ml-1">{generalFlows.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="webhook" className="flex items-center gap-2">
            <Webhook className="h-4 w-4" />
            Webhook
            <Badge variant="secondary" className="ml-1 bg-rose-500/10 text-rose-600">{webhookFlows.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* Aba Geral */}
        <TabsContent value="general">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <TabsList className="bg-muted/50 p-1 self-start">
                <TabsTrigger value="all" className="data-[state=active]:bg-background data-[state=active]:shadow-sm">
                  Todos ({generalFlows.length})
                </TabsTrigger>
                <TabsTrigger value="draft" className="data-[state=active]:bg-background data-[state=active]:shadow-sm">
                  Rascunhos ({generalFlows.filter(f => f.status === 'draft').length})
                </TabsTrigger>
                <TabsTrigger value="active" className="data-[state=active]:bg-background data-[state=active]:shadow-sm">
                  Ativos ({generalFlows.filter(f => f.status === 'active').length})
                </TabsTrigger>
                <TabsTrigger value="paused" className="data-[state=active]:bg-background data-[state=active]:shadow-sm">
                  Pausados ({generalFlows.filter(f => f.status === 'paused').length})
                </TabsTrigger>
              </TabsList>

              {/* Input de busca premium (mesmo padrão de "Todos os Negócios" — CrmDeals).
                  Filtra por nome, descrição ou código de 8 chars do UUID. */}
              <div className="relative flex-1 max-w-md md:mx-4">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
                <Input
                  placeholder="Buscar por nome, descrição ou código..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  aria-label="Buscar fluxos"
                  className="h-10 pl-10 pr-4 text-sm rounded-xl border-none bg-muted/40 hover:bg-muted/60 transition-all duration-200 focus-visible:bg-background focus-visible:shadow-md focus-visible:ring-1 focus-visible:ring-primary/20 focus-visible:ring-offset-0 placeholder:text-muted-foreground/50"
                />
              </div>

              <div className="flex items-center bg-muted/50 rounded-lg p-1 border border-border self-start md:self-auto">
                <button onClick={() => handleViewModeChange('cards')} className={cn('flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all', viewMode === 'cards' ? 'bg-foreground text-background shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button onClick={() => handleViewModeChange('list')} className={cn('flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all', viewMode === 'list' ? 'bg-foreground text-background shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
            <TabsContent value={activeTab} className="mt-4">
              {filteredFlows.length === 0 ? (
                <EmptyState onCreateFlow={() => navigate('/tenant/flows/new')} canCreate={canCreateFlow} />
              ) : viewMode === 'cards' ? (
                <FlowsGrid flows={filteredFlows} onEdit={handleEdit} onDuplicate={handleDuplicate} onDelete={handleDelete} onSettings={handleSettings} onExport={handleExport} canEditFlow={canEditFlow} canDeleteFlow={canDeleteFlow} />
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <div className="divide-y divide-border">
                      {filteredFlows.map((flow) => (
                        <FlowListRow key={flow.id} flow={flow} onEdit={handleEdit} onDuplicate={handleDuplicate} onDelete={handleDelete} onSettings={handleSettings} onWebhookConfig={setWebhookDetailFlow} onExport={handleExport} canEditFlow={canEditFlow} canDeleteFlow={canDeleteFlow} />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Aba Webhook */}
        <TabsContent value="webhook">
          <FlowWebhooksList
            flows={webhookFlows}
            loading={isLoading}
            canEdit={canEditFlow}
            canDelete={canDeleteFlow}
            onCreateFlow={() => navigate('/tenant/flows/new')}
            onOpenWebhookDetail={setWebhookDetailFlow}
            onEditFlow={(flow) => navigate(`/tenant/flows/${flow.id}`)}
            onDeleteFlow={handleDelete}
            onEditName={setEditingFlow}
            onConfigureTriggers={setConfiguringFlow}
            onDuplicate={handleDuplicate}
            onExport={handleExport}
          />
        </TabsContent>
      </Tabs>

      {/* Dialog de Edição */}
      <EditFlowDialog flow={editingFlow} open={!!editingFlow} onOpenChange={(open) => !open && setEditingFlow(null)} />

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={!!deletingFlow} onOpenChange={(open) => !open && setDeletingFlow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o fluxo "{deletingFlow?.name}"? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de Configuração de Gatilhos */}
      <ConfigureTriggersDialog
        open={!!configuringFlow}
        onOpenChange={(open) => !open && setConfiguringFlow(null)}
        triggers={Array.isArray(configuringFlow?.trigger_config) ? (configuringFlow.trigger_config as unknown as TriggerConfig[]) : []}
        onOpenWebhookDetail={configuringFlow ? () => setWebhookDetailFlow(configuringFlow) : undefined}
        onSave={async (triggers) => {
          if (configuringFlow) {
            // Passo 1: Salvar trigger_config no banco
            const { error } = await supabase
              .from('flows')
              .update({ trigger_config: triggers as unknown as Tables<'flows'>['trigger_config'] })
              .eq('id', configuringFlow.id);
            
            if (error) {
              toast({
                title: "Erro ao salvar gatilhos",
                description: error.message,
                variant: "destructive"
              });
              return;
            }
            
            // Passo 2: Gerar token automaticamente se há gatilho webhook e token não existe
            const hasWebhookTrigger = triggers.some((t: TriggerConfig) => t.type === 'webhook');
            if (hasWebhookTrigger && !configuringFlow.webhook_token) {
              console.log('[Flows] Detectado gatilho webhook sem token, gerando automaticamente...');
              const { data: newToken, error: tokenError } = await supabase.rpc('regenerate_flow_webhook_token', {
                p_flow_id: configuringFlow.id
              });
              
              if (tokenError) {
                console.error('[Flows] Erro ao gerar token de webhook:', tokenError);
                toast({
                  title: "Aviso",
                  description: "Gatilhos salvos, mas erro ao gerar token do webhook. Clique em 'Webhook de Integração' para regenerar.",
                });
              } else {
                console.log('[Flows] Token de webhook gerado com sucesso:', newToken?.substring(0, 10) + '...');
              }
            }
            
            // Passo 3: Invalidar cache e fechar modal
            queryClient.invalidateQueries({ queryKey: ['flows'] });
            toast({ title: 'Gatilhos salvos com sucesso' });
            setConfiguringFlow(null);
          }
        }}
      />

      {/* Dialog de detalhes de execução - tenant obtido internamente no backend */}
      <ExecutionDetailsDialog
        open={executionDialogOpen}
        onOpenChange={setExecutionDialogOpen}
        mode={executionDialogMode}
      />

      {/* Dialog de detalhes do webhook de integração */}
      <FlowWebhookDetailDialog
        open={!!webhookDetailFlow}
        onOpenChange={(open) => !open && setWebhookDetailFlow(null)}
        flow={webhookDetailFlow}
      />

      {/* Dialog de exportação de fluxo */}
      {/* Exportação do fluxo pelo assistente de templates, já filtrado */}
      <TemplateExportDialog
        open={!!exportingFlow}
        onOpenChange={(open) => !open && setExportingFlow(null)}
        onCreated={() => setExportingFlow(null)}
        initialModules={exportSelectionModules}
        initialName={exportingFlow?.name ?? undefined}
        buildInitialSelection={buildExportSelection}
      />

      {/* Dialog de importação de fluxo */}
      <ImportFlowDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
      />
    </div>
  );
}

/**
 * Card de métrica com design moderno
 * Aceita onClick opcional para tornar o card clicável
 */
/** Props do MetricCard de Automações */
interface MetricCardProps {
  title: string;
  value: string | number;
  icon: typeof Play;
  variant: 'primary' | 'blue' | 'green' | 'red';
  onClick?: () => void;
  clickable?: boolean;
}

/** Mapeamento de variantes para cores (padrão DealListKpiCards) */
const flowVariantStyles = {
  primary: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  blue: { cardBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-700 dark:text-blue-300' },
  green: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  red: { cardBg: 'bg-pink-100 dark:bg-pink-900/30', iconColor: 'text-pink-700 dark:text-pink-300' },
};

/** Card de KPI compacto horizontal (padrão unificado DealListKpiCards) */
function MetricCard({ title, value, icon: Icon, variant, onClick, clickable }: MetricCardProps) {
  const styles = flowVariantStyles[variant];
  return (
    <Card
      className={cn(
        'border-0 shadow-none transition-all hover:shadow-md',
        styles.cardBg,
        clickable && 'cursor-pointer'
      )}
      onClick={onClick}
    >
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={cn('h-5 w-5 shrink-0', styles.iconColor)} />
            <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">{title}</p>
          </div>
          <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Estado vazio quando não há fluxos
 * Botão de criar só aparece se usuário tem permissão
 */
function EmptyState({ onCreateFlow, canCreate }: { onCreateFlow: () => void; canCreate?: boolean }) {
  return (
    <Card className="border-dashed border-2 bg-gradient-to-br from-muted/30 to-muted/10">
      <CardContent className="flex flex-col items-center justify-center py-20">
        <div className="flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-6">
          <GitBranch className="h-10 w-10 text-primary/70" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Nenhum fluxo criado</h3>
        <p className="text-muted-foreground mb-6 text-center max-w-md">
          {canCreate 
            ? 'Crie seu primeiro fluxo de automação para responder automaticamente seus clientes'
            : 'Nenhum fluxo de automação foi criado ainda'
          }
        </p>
        {canCreate && (
          <Button 
            size="xs"
            onClick={onCreateFlow}
            className="transition-all hover:shadow-md hover:scale-[1.02]"
          >
            <Plus className="mr-1.5" />
            Criar Primeiro Fluxo
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Grid de cards de fluxos
 * Passa permissões para cada card
 */
interface FlowsGridProps {
  flows: Tables<'flows'>[];
  onEdit: (flow: Tables<'flows'>) => void;
  onDuplicate: (flow: Tables<'flows'>) => void;
  onDelete: (flow: Tables<'flows'>) => void;
  onSettings: (flow: Tables<'flows'>) => void;
  onExport: (flow: Tables<'flows'>) => void;
  canEditFlow?: boolean;
  canDeleteFlow?: boolean;
}

function FlowsGrid({ flows, onEdit, onDuplicate, onDelete, onSettings, onExport, canEditFlow, canDeleteFlow }: FlowsGridProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {flows.map((flow) => (
        <FlowCard 
          key={flow.id} 
          flow={flow} 
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          onSettings={onSettings}
          onExport={onExport}
          canEditFlow={canEditFlow}
          canDeleteFlow={canDeleteFlow}
        />
      ))}
    </div>
  );
}

/**
 * Card individual de fluxo com design moderno
 * Ações condicionadas às permissões do usuário
 */
interface FlowCardProps {
  flow: Tables<'flows'>;
  onEdit: (flow: Tables<'flows'>) => void;
  onDuplicate: (flow: Tables<'flows'>) => void;
  onDelete: (flow: Tables<'flows'>) => void;
  onSettings: (flow: Tables<'flows'>) => void;
  onExport: (flow: Tables<'flows'>) => void;
  canEditFlow?: boolean;
  canDeleteFlow?: boolean;
}

function FlowCard({ flow, onEdit, onDuplicate, onDelete, onSettings, onExport, canEditFlow, canDeleteFlow }: FlowCardProps) {
  const navigate = useNavigate();
  const config = statusConfig[flow.status] || statusConfig.draft;
  const StatusIcon = config.icon;

  // Conta o número de nós do fluxo
  const nodeCount = useMemo(() => {
    const nodes = flow.nodes as unknown as Array<unknown>;
    return Array.isArray(nodes) ? nodes.length : 0;
  }, [flow.nodes]);

  return (
    <Card 
      className={`group relative overflow-hidden cursor-pointer border-t-4 ${config.borderColor} hover:shadow-lg hover:-translate-y-1 transition-all duration-300`}
      onClick={() => navigate(`/tenant/flows/${flow.id}`)}
    >
      {/* Efeito shimmer no hover */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
      
      <CardHeader className="relative pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <CardTitle className="text-base truncate">{flow.name}</CardTitle>
              {/* Código identificador: 8 primeiros chars do UUID em uppercase.
                  Selecionável e isolado do clique do card. */}
              <code
                className="shrink-0 px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono font-semibold text-muted-foreground tracking-wider select-text cursor-text"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {flow.id.substring(0, 8).toUpperCase()}
              </code>
            </div>
            <CardDescription className="mt-1 line-clamp-2">
              {flow.description || 'Sem descrição'}
            </CardDescription>
          </div>
          
          {/* Badge de status com glow */}
          <Badge className={`shrink-0 ${config.badgeClass} ${config.glowClass}`}>
            <StatusIcon className="h-3 w-3 mr-1" />
            {config.label}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="relative pt-0">
        {/* Metadados do fluxo */}
        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground mb-4">
          <div className="flex items-center gap-1.5">
            <Boxes className="h-3.5 w-3.5" />
            <span>{nodeCount} {nodeCount === 1 ? 'bloco' : 'blocos'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            <span>Criado em {format(new Date(flow.created_at), "dd/MM/yy", { locale: ptBR })}</span>
          </div>
        </div>

        {/* Menu de ações */}
        <div className="flex items-center justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              {/* Visualizar - sempre disponível */}
              <DropdownMenuItem onClick={() => navigate(`/tenant/flows/${flow.id}`)}>
                <Eye className="h-4 w-4 mr-2" />
                Visualizar
              </DropdownMenuItem>
              
              {/* Exportar - sempre disponível */}
              <DropdownMenuItem onClick={() => onExport(flow)}>
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </DropdownMenuItem>
              
              {/* Ações de edição - requerem permissão */}
              {canEditFlow && (
                <>
                  <DropdownMenuItem onClick={() => onEdit(flow)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Editar nome
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onSettings(flow)}>
                    <Settings className="h-4 w-4 mr-2" />
                    Configurar gatilhos
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onDuplicate(flow)}>
                    <Copy className="h-4 w-4 mr-2" />
                    Duplicar
                  </DropdownMenuItem>
                </>
              )}
              
              {/* Excluir - requer permissão de delete */}
              {canDeleteFlow && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => onDelete(flow)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Excluir
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
}
